// ──────────────────────────────────────────────────────────────
//  metricCharts.ts — Shared value formatting and ApexCharts option
//  building for every analytics visualization.
//
//  Used by both the Metrics query builder and the dashboard widgets,
//  so the same metric renders identically wherever it appears.
// ──────────────────────────────────────────────────────────────

import type { AnalyticsField, AnalyticsLinkKind, AnalyticsPoint, AnalyticsSeries } from '../AppTypes';

/**
 * Builds the admin route for a grouped value, or null when it cannot be linked.
 *
 * Every linkable field groups by the object's **numeric id**, which is exactly what
 * the admin routes are keyed on.
 *
 * @param kind  - What the value identifies.
 * @param value - The group value: the object's id.
 * @returns The client route, or null when there is nothing to link to.
 */
export function analyticsObjectLink(kind: AnalyticsLinkKind | undefined, value: string | null): string | null {
	if (kind == null || value == null || value.length === 0) return null;

	/* Each kind has its own admin route; a role has no page of its own. */
	switch (kind) {
		case 'user':
			return `/Users/${encodeURIComponent(value)}`;
		case 'application':
			return `/Applications/${encodeURIComponent(value)}`;
		case 'newsletter':
			return `/Newsletters/${encodeURIComponent(value)}/edit`;
		case 'role':
			/* Roles have no page of their own — the list is not addressable per role. */
			return null;
		default:
			return null;
	}
}

/**
 * Formats a number of bytes as a human-readable size.
 *
 * @param bytes - The byte count.
 * @returns A string such as `4.2 GB`.
 */
export function formatBytes(bytes: number): string {
	if (bytes <= 0) return '0 B';
	const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
	const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
	const value = bytes / Math.pow(1024, exponent);
	return `${value.toFixed(value >= 100 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

/**
 * Formats a number of seconds as a human-readable span.
 *
 * @param seconds - The duration.
 * @returns A string such as `2.3 d`, `4 h` or `15 m`.
 */
export function formatDuration(seconds: number): string {
	if (!Number.isFinite(seconds) || seconds <= 0) return '0 s';
	const units: [string, number][] = [
		['d', 86400],
		['h', 3600],
		['m', 60],
		['s', 1]
	];
	/* Walk from the coarsest unit down; the first the value reaches is the one to use. */
	for (const [label, size] of units) {
		/* The first unit the value reaches is the one to express it in. */
		if (seconds >= size) {
			const value = seconds / size;
			return `${value.toFixed(value >= 100 || size === 1 ? 0 : 1)} ${label}`;
		}
	}
	return `${Math.round(seconds)} s`;
}

/**
 * Formats a value for display according to its metric's unit.
 *
 * @param value - The raw value.
 * @param unit  - The metric unit.
 * @returns The formatted value.
 */
export function formatValue(value: number, unit: string): string {
	/* Each unit has its own reading; anything else is a plain localized number. */
	if (unit === 'bytes') return formatBytes(value);
	/* Both duration units read as a human span. */
	if (unit === 'duration') return formatDuration(value);
	/* As above, in the other unit the engine can emit. */
	if (unit === 'milliseconds') return formatDuration(value / 1000);
	/* A percentage keeps a decimal only while it is small enough to matter. */
	if (unit === 'percent') return `${value.toFixed(value >= 10 || value === 0 ? 0 : 1)}%`;
	/* Money carries its symbol, and loses the cents once it is large. */
	if (unit === 'currency') {
		return value.toLocaleString(undefined, {
			style: 'currency',
			currency: 'USD',
			maximumFractionDigits: value >= 100 ? 0 : 2
		});
	}
	return value.toLocaleString();
}

/** One dimension of a point's group, with the route to its object when it has one. */
export interface GroupSegment {
	/** The group field key. */
	key: string;
	/** The display text. */
	label: string;
	/** The route to the object's page, or null when this dimension is not linkable. */
	href: string | null;
}

/**
 * Splits a point's group into its dimensions, resolving a route for each one that
 * identifies a real object. This is what lets a grouped row link through to the user
 * or application it counts.
 *
 * @param point  - The point whose group to describe.
 * @param fields - The metric's field definitions, which say which dimensions are linkable.
 * @returns One segment per group dimension.
 */
export function groupSegments(point: AnalyticsPoint, fields: AnalyticsField[]): GroupSegment[] {
	return Object.keys(point.Group).map((key) => {
		const field = fields.find((candidate) => candidate.Key === key);
		return {
			key,
			label: point.GroupLabel?.[key] ?? point.Group[key] ?? '—',
			href: analyticsObjectLink(field?.Link?.Kind, point.Group[key])
		};
	});
}

/**
 * Builds a human-readable label for a point's group combination.
 *
 * @param point - The point to label.
 * @returns The joined label, or an empty string when the point is not grouped.
 */
export function groupLabel(point: AnalyticsPoint): string {
	const keys = Object.keys(point.Group);
	if (keys.length === 0) return '';
	return keys.map((key) => point.GroupLabel[key] ?? point.Group[key] ?? '—').join(' / ');
}

// ── Time-bucket axis helpers (filling dates with no data) ──────

/**
 * Parses a backend bucket label into a UTC date.
 *
 * @param label - `YYYY-MM-DD`, `YYYY-MM-01` or `YYYY-MM-DD HH:00:00`.
 * @returns The bucket start.
 */
export function parseBucket(label: string): Date {
	const [datePart, timePart] = label.split(' ');
	const [year, month, day] = datePart.split('-').map(Number);
	const hour = timePart != null ? Number(timePart.split(':')[0]) : 0;
	return new Date(Date.UTC(year, month - 1, day, hour));
}

/**
 * Formats a UTC date back into the bucket label for a granularity.
 *
 * @param date        - The bucket start.
 * @param granularity - The bucket granularity.
 * @returns The label, matching what the server emits.
 */
export function formatBucket(date: Date, granularity: string): string {
	const year = date.getUTCFullYear();
	const month = String(date.getUTCMonth() + 1).padStart(2, '0');
	const day = String(date.getUTCDate()).padStart(2, '0');
	const hour = String(date.getUTCHours()).padStart(2, '0');
	/* Hour and month label differently; day and week both label by their start date. */
	if (granularity === 'hour') return `${year}-${month}-${day} ${hour}:00:00`;
	/* A month always labels as its first day, matching the server's own format. */
	if (granularity === 'month') return `${year}-${month}-01`;
	/* Day and (Monday-anchored) week both label by their start date. */
	return `${year}-${month}-${day}`;
}

/**
 * Snaps a date down to the start of its bucket.
 *
 * @param date        - The date to snap.
 * @param granularity - The bucket granularity.
 * @returns The bucket start.
 */
export function alignBucket(date: Date, granularity: string): Date {
	const aligned = new Date(date.getTime());
	aligned.setUTCMinutes(0, 0, 0);
	/* An hour bucket is already aligned once the minutes are cleared. */
	if (granularity === 'hour') return aligned;
	aligned.setUTCHours(0);
	/* A week snaps back to its Monday; a month to its first. */
	if (granularity === 'week') {
		/* Back to Monday, matching the server's DATE_TRUNC('week'). */
		aligned.setUTCDate(aligned.getUTCDate() - ((aligned.getUTCDay() + 6) % 7));
	} else if (granularity === 'month') {
		aligned.setUTCDate(1);
	}
	return aligned;
}

/**
 * Advances a date by one bucket.
 *
 * @param date        - The current bucket start.
 * @param granularity - The bucket granularity.
 * @returns The next bucket start.
 */
export function nextBucket(date: Date, granularity: string): Date {
	const next = new Date(date.getTime());
	/* One step of whichever unit the axis is drawn in. */
	if (granularity === 'hour') next.setUTCHours(next.getUTCHours() + 1);
	else if (granularity === 'week') next.setUTCDate(next.getUTCDate() + 7);
	else if (granularity === 'month') next.setUTCMonth(next.getUTCMonth() + 1);
	else next.setUTCDate(next.getUTCDate() + 1);
	return next;
}

/**
 * Builds a continuous, gap-free list of bucket labels covering the data.
 *
 * Without this a chart would join the two sides of a quiet week into one straight
 * line, which reads as steady activity rather than as none.
 *
 * @param presentLabels - The bucket labels the data actually carries.
 * @param granularity   - The bucket granularity.
 * @param fromDate      - Optional lower bound to extend the axis to.
 * @param toDate        - Optional upper bound to extend the axis to.
 * @returns Every bucket label between the bounds, in order.
 */
export function buildBucketAxis(
	presentLabels: string[],
	granularity: string,
	fromDate: string,
	toDate: string
): string[] {
	if (presentLabels.length === 0) return [];
	const aligned = presentLabels.map((label) => alignBucket(parseBucket(label), granularity).getTime());
	let startMs = Math.min(...aligned);
	let endMs = Math.max(...aligned);
	/* Pad the axis out to the requested range, where one was given. */
	if (fromDate.length > 0) startMs = Math.min(startMs, alignBucket(parseBucket(fromDate), granularity).getTime());
	/* The same at the other end, so an empty tail still shows as empty. */
	if (toDate.length > 0) endMs = Math.max(endMs, alignBucket(parseBucket(toDate), granularity).getTime());

	const axis: string[] = [];
	let cursor = new Date(startMs);
	/* Bounded so a malformed range cannot spin forever. */
	for (let guard = 0; cursor.getTime() <= endMs && guard < 6000; guard++) {
		axis.push(formatBucket(cursor, granularity));
		cursor = nextBucket(cursor, granularity);
	}
	return axis;
}

/** The theme-aware colours a chart needs to stay legible in both themes. */
export interface ChartTheme {
	/** ApexCharts theme mode. */
	mode: string;
	/** Axis and legend text colour. */
	text: string;
	/** Gridline colour. */
	grid: string;
	/** Tooltip theme. */
	tooltip: string;
	/** The series palette. */
	colors: string[];
}

/**
 * Resolves the chart palette for the current theme.
 *
 * The app toggles a `dark` class on the document element, so that is what decides —
 * reading it per chart rather than caching it means a theme switch is picked up by
 * the next render with no subscription.
 *
 * @returns The colours to build chart options with.
 */
export function chartTheme(): ChartTheme {
	const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
	return {
		mode: isDark ? 'dark' : 'light',
		/* zinc-300 / zinc-700, matching the app's re-pointed gray ramp. */
		text: isDark ? '#d4d4d8' : '#3f3f46',
		grid: isDark ? '#3f3f46' : '#e4e4e7',
		tooltip: isDark ? 'dark' : 'light',
		/*
		 * Abstract red leads, then a spread that stays distinguishable on both
		 * backgrounds and for the common forms of colour blindness.
		 */
		colors: ['#dc0900', '#f59e0b', '#3b82f6', '#22c55e', '#a855f7', '#06b6d4', '#ec4899', '#84cc16']
	};
}

/** A vertical marker drawn on a time-series chart. */
export interface ChartAnnotation {
	/** `YYYY-MM-DD`. */
	date: string;
	label: string;
}

/** Context the chart builder needs that is owned by the caller. */
export interface ChartContext {
	granularity: string;
	fromDate: string;
	toDate: string;
	/** The fill strategy (`zero`, `gap` or `repeat`) for a metric, from the catalogue. */
	metricFill: (metricKey: string) => string;
	/** The localized label for a single-value chart's one series. */
	valueLabel: string;
	/** The chart height in pixels. Defaults to 360. */
	height?: number;
	/**
	 * `area` produces the compact dashboard look: gradient fill, no markers, no axis
	 * labels and no toolbar. `default` — the Metrics page — keeps crisp lines with
	 * markers and full axes.
	 */
	style?: 'default' | 'area';
	/** Overrides the series type. Defaults by {@link style}. */
	chartType?: string;
	/** Vertical markers to draw on the time axis. */
	annotations?: ChartAnnotation[];
	/** A horizontal goal reference line on the value axis. */
	goal?: number;
}

/** Named threshold colours; a hex value passes through unchanged. */
const kThresholdColors: Record<string, string> = { green: '#22c55e', yellow: '#f59e0b', red: '#ef4444' };

/**
 * Resolves a threshold colour name to its hex value.
 *
 * @param name - A colour name or a hex string.
 * @returns The hex value.
 */
export function thresholdColor(name: string): string {
	return kThresholdColors[name] ?? name;
}

/**
 * Resolves the colour a value should take given a set of thresholds and a direction.
 *
 * For `above` the highest crossed bound wins; for `below`, the lowest.
 *
 * @param thresholds - The configured thresholds.
 * @param value      - The value to colour.
 * @param direction  - Whether a threshold trips above or below its bound.
 * @returns The hex colour, or null when no threshold is crossed.
 */
export function resolveThreshold(
	value: number,
	thresholds: { value: number; color: string }[] | undefined,
	direction: string | undefined
): string | null {
	if (thresholds == null || thresholds.length === 0) return null;
	const below = direction === 'below';
	const sorted = [...thresholds].sort((a, b) => (below ? b.value - a.value : a.value - b.value));
	let picked: string | null = null;
	/* Evaluated in order, so the last one crossed is the one that wins. */
	for (const threshold of sorted) {
		const crossed = below ? value <= threshold.value : value >= threshold.value;
		if (crossed) picked = thresholdColor(threshold.color);
	}
	return picked;
}

/**
 * Builds compact options for the sparkline shown beneath a headline value.
 *
 * @param values - One value per bucket.
 * @param unit   - The metric unit, for the tooltip.
 * @param color  - An optional override, usually a tripped threshold colour.
 * @returns ApexCharts options.
 */
export function buildSparklineOptions(values: (number | null)[], unit: string, color?: string): Record<string, unknown> {
	const theme = chartTheme();
	return {
		chart: {
			type: 'bar',
			height: 48,
			sparkline: { enabled: true },
			animations: { enabled: false },
			background: 'transparent'
		},
		series: [{ name: '', data: values }],
		colors: [color ?? theme.colors[0]],
		plotOptions: { bar: { columnWidth: '70%', borderRadius: 1, borderRadiusApplication: 'end' } },
		dataLabels: { enabled: false },
		tooltip: {
			enabled: true,
			theme: theme.tooltip,
			x: { show: false },
			y: { formatter: (value: number) => formatValue(value, unit) }
		}
	};
}

/**
 * Builds radial-gauge options showing a value against a target.
 *
 * @param value  - The current value.
 * @param target - The value treated as 100%.
 * @param unit   - The metric unit.
 * @param color  - An optional override.
 * @param height - The chart height.
 * @returns ApexCharts options.
 */
export function buildGaugeOptions(
	value: number,
	target: number,
	unit: string,
	color?: string,
	height = 240
): Record<string, unknown> {
	const theme = chartTheme();
	/* A target of zero has no percentage; show an empty ring rather than dividing by it. */
	const percent = target > 0 ? Math.max(0, Math.min(100, (value / target) * 100)) : 0;
	return {
		chart: { type: 'radialBar', height, background: 'transparent', animations: { enabled: false } },
		series: [Math.round(percent * 10) / 10],
		colors: [color ?? theme.colors[0]],
		theme: { mode: theme.mode },
		plotOptions: {
			radialBar: {
				hollow: { size: '62%' },
				track: { background: theme.grid },
				dataLabels: {
					name: { show: true, color: theme.text, fontSize: '12px', offsetY: 22 },
					value: {
						show: true,
						color: theme.text,
						fontSize: '26px',
						fontWeight: 700,
						offsetY: -8,
						formatter: () => formatValue(value, unit)
					}
				}
			}
		},
		labels: [target > 0 ? `of ${formatValue(target, unit)}` : ''],
		stroke: { lineCap: 'round' }
	};
}

/**
 * Builds a waterfall chart from a set of metric totals, showing how each value adds to
 * or subtracts from a running total.
 *
 * Implemented as a stacked bar with a transparent floating base: positive bars green,
 * negative red.
 *
 * @param totals      - One entry per metric, in order.
 * @param unit        - The metric unit.
 * @param height      - The chart height.
 * @param staticChart - Extra chart options, used to strip the toolbar on a dashboard tile.
 * @returns ApexCharts options.
 */
export function buildWaterfallOptions(
	totals: { label: string; value: number }[],
	unit: string,
	height = 240,
	staticChart: Record<string, unknown> = {}
): Record<string, unknown> {
	const theme = chartTheme();
	/** Formats a bar's value in the metric's unit. */
	const fnValueFormatter = (value: number): string => formatValue(value, unit);
	let running = 0;
	const base: number[] = [];
	const bars: { x: string; y: number; fillColor: string }[] = [];
	/* Each bar starts where the last one ended, so the base carries the running total. */
	for (const total of totals) {
		/* A rise floats on the running total; a fall hangs beneath the new one. */
		if (total.value >= 0) {
			base.push(running);
			bars.push({ x: total.label, y: total.value, fillColor: '#22c55e' });
			running += total.value;
		} else {
			running += total.value;
			base.push(running);
			bars.push({ x: total.label, y: -total.value, fillColor: '#ef4444' });
		}
	}
	return {
		chart: { type: 'bar', stacked: true, height, background: 'transparent', foreColor: theme.text, ...staticChart },
		theme: { mode: theme.mode },
		series: [
			{ name: 'base', data: base },
			{ name: 'value', data: bars }
		],
		colors: ['transparent', '#22c55e'],
		fill: { opacity: [0, 1] },
		plotOptions: { bar: { borderRadius: 2, columnWidth: '60%' } },
		dataLabels: { enabled: false },
		grid: { borderColor: theme.grid, strokeDashArray: 4 },
		xaxis: { axisBorder: { color: theme.grid }, axisTicks: { color: theme.grid } },
		yaxis: { labels: { formatter: fnValueFormatter } },
		legend: { show: false },
		tooltip: {
			theme: theme.tooltip,
			/* Only the visible bar, never the transparent base it floats on. */
			enabledOnSeries: [1],
			y: { formatter: fnValueFormatter }
		}
	};
}

/**
 * Serialises result series to CSV.
 *
 * @param series - The series to serialise.
 * @returns A CSV document with a Metric/Bucket/Group/Value header.
 */
export function seriesToCsv(series: AnalyticsSeries[]): string {
	/** Quotes a field when it carries a comma, quote or newline. */
	const fnEscape = (text: string): string => (/[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text);
	const rows: string[] = [['Metric', 'Bucket', 'Group', 'Value'].join(',')];
	/* One row per point, flattened across every series. */
	for (const entry of series) {
		for (const point of entry.Points) {
			rows.push(
				[fnEscape(entry.Label), fnEscape(point.Bucket ?? ''), fnEscape(groupLabel(point)), String(point.Value)].join(
					','
				)
			);
		}
	}
	return rows.join('\n');
}

/**
 * Triggers a browser download of arbitrary text content.
 *
 * @param filename - The name to save as.
 * @param content  - The file body.
 * @param mime     - The media type.
 */
export function downloadFile(filename: string, content: string, mime = 'text/plain'): void {
	const blob = new Blob([content], { type: mime });
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = filename;
	anchor.click();
	setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Builds x-axis annotations, snapping each marker to a bucket that exists on the axis.
 *
 * @param items       - The markers to draw.
 * @param buckets     - The axis labels.
 * @param granularity - The bucket granularity.
 * @param theme       - The resolved chart theme.
 * @returns The annotations object, or undefined when none land on the axis.
 */
function buildAnnotations(
	items: ChartAnnotation[] | undefined,
	buckets: string[],
	granularity: string,
	theme: ChartTheme
): Record<string, unknown> | undefined {
	if (items == null || items.length === 0) return undefined;
	const present = new Set(buckets);
	const xaxis = items
		.map((annotation) => {
			const label = formatBucket(alignBucket(parseBucket(annotation.date), granularity), granularity);
			/* A marker outside the visible range has nowhere to sit. */
			if (!present.has(label)) return null;
			return {
				x: label,
				strokeDashArray: 3,
				borderColor: theme.text,
				label: {
					text: annotation.label,
					orientation: 'horizontal' as const,
					style: { fontSize: '10px', color: '#ffffff', background: theme.text }
				}
			};
		})
		.filter((annotation) => annotation != null);
	return xaxis.length > 0 ? { xaxis } : undefined;
}

/** Chart types that render a categorical distribution rather than a time or category axis. */
const kDistributionTypes = new Set(['pie', 'donut', 'radar', 'polarArea']);

/**
 * Builds options for a distribution chart. The points are aggregated by group label
 * into one value per slice or spoke, so any time axis is summed away.
 *
 * @param series         - The result series.
 * @param context        - The caller's chart context.
 * @param theme          - The resolved chart theme.
 * @param height         - The chart height.
 * @param fnValueFormatter - The unit-aware formatter.
 * @param staticChart    - Extra chart options.
 * @returns ApexCharts options.
 */
function buildDistributionOptions(
	series: AnalyticsSeries[],
	context: ChartContext,
	theme: ChartTheme,
	height: number,
	fnValueFormatter: (value: number) => string,
	staticChart: Record<string, unknown>
): Record<string, unknown> {
	const type = context.chartType as string;
	/* buildChartOptions has already returned for an empty series, so there is always a first. */
	const primary = series[0];

	let labels: string[];
	let values: number[];
	/* Several metrics give one slice each; a single grouped metric gives one per group. */
	if (series.length > 1) {
		labels = series.map((entry) => entry.Label);
		values = series.map((entry) => entry.Points.reduce((sum, point) => sum + point.Value, 0));
	} else {
		const byLabel = new Map<string, number>();
		for (const point of primary.Points) {
			const label = groupLabel(point) || primary.Label;
			byLabel.set(label, (byLabel.get(label) ?? 0) + point.Value);
		}
		labels = [...byLabel.keys()];
		values = [...byLabel.values()];
	}

	/* A radar plots one polygon over the category spokes rather than slices. */
	if (type === 'radar') {
		return {
			chart: { type: 'radar', height, background: 'transparent', foreColor: theme.text, ...staticChart },
			theme: { mode: theme.mode },
			colors: theme.colors,
			series: [{ name: series.length > 1 ? '' : primary.Label, data: values }],
			labels,
			xaxis: { categories: labels },
			yaxis: { labels: { show: false } },
			stroke: { width: 2 },
			fill: { opacity: 0.2 },
			markers: { size: 3 },
			dataLabels: { enabled: false },
			tooltip: { theme: theme.tooltip, y: { formatter: fnValueFormatter } },
			legend: { show: false }
		};
	}

	return {
		chart: { type, height, background: 'transparent', foreColor: theme.text, ...staticChart },
		theme: { mode: theme.mode },
		colors: theme.colors,
		series: values,
		labels,
		stroke: { width: 1, colors: [theme.mode === 'dark' ? '#18181b' : '#ffffff'] },
		dataLabels: { enabled: true },
		tooltip: { theme: theme.tooltip, y: { formatter: fnValueFormatter } },
		legend: { position: 'bottom', labels: { colors: theme.text } },
		...(type === 'donut' ? { plotOptions: { pie: { donut: { size: '62%' } } } } : {})
	};
}

/**
 * Builds the ApexCharts options for a set of result series: a gap-filled time-series
 * chart for bucketed metrics, a distribution chart when one is asked for, or a bar
 * chart for snapshot, grouped and single-value results.
 *
 * @param series  - The result series to plot.
 * @param context - The caller's chart context.
 * @returns ApexCharts options, or null when there is nothing to draw.
 */
export function buildChartOptions(series: AnalyticsSeries[], context: ChartContext): Record<string, unknown> | null {
	if (series.length === 0) return null;

	const height = context.height ?? 360;
	const primaryUnit = series[0].Unit;
	/** Formats a value in the unit of the first series, which the axis is labelled in. */
	const fnValueFormatter = (value: number): string => formatValue(value, primaryUnit);
	const theme = chartTheme();
	const isArea = context.style === 'area';

	/*
	 * The compact dashboard style drops the toolbar, zoom, pan and selection. Hovering
	 * still shows the value, but the series-dimming highlight is suppressed — on a small
	 * tile it reads as the chart breaking rather than as a hover state.
	 */
	const staticChart = isArea
		? {
				toolbar: { show: false },
				zoom: { enabled: false },
				selection: { enabled: false },
				animations: { enabled: false }
			}
		: { toolbar: { show: true }, zoom: { enabled: true } };
	const staticExtras = isArea
		? { states: { hover: { filter: { type: 'none' } }, active: { filter: { type: 'none' } } } }
		: {};
	/*
	 * On a dashboard tile the plot grips the left card edge; only the right gets padding,
	 * to balance the gutter ApexCharts leaves on category line and area charts so both
	 * sides read evenly.
	 */
	const gridPadding = isArea ? { padding: { left: 0, right: 12 } } : {};

	/* A distribution chart collapses the time axis into one slice per group. */
	if (context.chartType != null && kDistributionTypes.has(context.chartType)) {
		return buildDistributionOptions(series, context, theme, height, fnValueFormatter, staticChart);
	}

	const isTimeChart =
		series.every((entry) => entry.TimeSeries) &&
		series.some((entry) => entry.Points.some((point) => point.Bucket != null));

	/* A time chart draws a continuous axis; everything else draws categories. */
	if (isTimeChart) {
		const present = new Set<string>();
		for (const entry of series) {
			for (const point of entry.Points) {
				if (point.Bucket != null) present.add(point.Bucket);
			}
		}
		/*
		 * A dashboard tile spans the axis to the data extent so the line grips both card
		 * edges; the Metrics page pads it out to the full requested range, where an empty
		 * tail is itself the answer.
		 */
		const buckets = isArea
			? buildBucketAxis([...present], context.granularity, '', '')
			: buildBucketAxis([...present], context.granularity, context.fromDate, context.toDate);
		const markerAnnotations = buildAnnotations(context.annotations, buckets, context.granularity, theme);
		const goalLine =
			context.goal != null
				? {
						yaxis: [
							{
								y: context.goal,
								borderColor: '#22c55e',
								strokeDashArray: 4,
								label: {
									text: `${fnValueFormatter(context.goal)}`,
									style: { fontSize: '10px', color: '#ffffff', background: '#22c55e' }
								}
							}
						]
					}
				: undefined;
		const annotations =
			markerAnnotations != null || goalLine != null
				? { ...(markerAnnotations ?? {}), ...(goalLine ?? {}) }
				: undefined;

		const chartSeries: { name: string; data: (number | null)[] }[] = [];
		for (const entry of series) {
			const fill = context.metricFill(entry.Metric);
			const byGroup: Map<string, Map<string, number>> = new Map();
			for (const point of entry.Points) {
				if (point.Bucket == null) continue;
				const key = groupLabel(point);
				if (!byGroup.has(key)) byGroup.set(key, new Map());
				byGroup.get(key)!.set(point.Bucket, point.Value);
			}
			for (const [key, bucketMap] of byGroup) {
				let last: number | null = null;
				const data = buckets.map((bucket) => {
					/* A bucket the data carries sets the value, and the carry-forward. */
					if (bucketMap.has(bucket)) {
						last = bucketMap.get(bucket)!;
						return last;
					}
					/* An empty bucket is filled per the metric's own strategy. */
					if (fill === 'zero') return 0;
					/* A cumulative line carries its last value forward rather than dropping. */
					if (fill === 'repeat') return last;
					return null;
				});
				chartSeries.push({ name: key.length > 0 ? `${entry.Label} — ${key}` : entry.Label, data });
			}
		}

		const seriesType = context.chartType ?? (isArea ? 'area' : 'line');
		const isBar = seriesType === 'bar';

		return {
			chart: { type: seriesType, height, background: 'transparent', foreColor: theme.text, ...staticChart },
			theme: { mode: theme.mode },
			colors: theme.colors,
			series: chartSeries,
			dataLabels: { enabled: false },
			...(seriesType === 'area'
				? {
						fill: {
							type: 'gradient',
							gradient: { shadeIntensity: 1, opacityFrom: 0.45, opacityTo: 0.05, stops: [0, 90, 100] }
						}
					}
				: {}),
			...(isBar
				? { plotOptions: { bar: { borderRadius: 3, borderRadiusApplication: 'end', columnWidth: '80%' } } }
				: {}),
			grid: {
				borderColor: theme.grid,
				strokeDashArray: 4,
				xaxis: { lines: { show: false } },
				yaxis: { lines: { show: true } },
				...gridPadding
			},
			xaxis: {
				categories: buckets,
				labels: { show: !isArea },
				axisBorder: { show: !isArea, color: theme.grid },
				axisTicks: { show: !isArea, color: theme.grid }
			},
			yaxis: { labels: { show: !isArea, formatter: fnValueFormatter } },
			stroke: { curve: 'straight', width: isBar ? 0 : 2 },
			markers: { size: isArea || isBar ? 0 : 3, strokeWidth: 0, hover: { size: isArea || isBar ? 0 : 5 } },
			tooltip: { theme: theme.tooltip, y: { formatter: fnValueFormatter } },
			legend: { position: 'bottom', labels: { colors: theme.text } },
			...(annotations != null ? { annotations } : {}),
			...staticExtras
		};
	}

	const categorySet = new Set<string>();
	/* Not a time chart: the categories are whatever groups the points carry. */
	for (const entry of series) {
		for (const point of entry.Points) categorySet.add(groupLabel(point));
	}
	const categories = [...categorySet];
	const hasGroups = categories.some((category) => category.length > 0);

	/* Snapshot but grouped: a bar per group. */
	if (hasGroups) {
		const chartSeries = series.map((entry) => {
			const byGroup = new Map<string, number>();
			for (const point of entry.Points) byGroup.set(groupLabel(point), point.Value);
			return { name: entry.Label, data: categories.map((category) => byGroup.get(category) ?? 0) };
		});
		const snapshotType = context.chartType === 'line' || context.chartType === 'area' ? context.chartType : 'bar';
		return {
			chart: { type: snapshotType, height, background: 'transparent', foreColor: theme.text, ...staticChart },
			theme: { mode: theme.mode },
			colors: theme.colors,
			series: chartSeries,
			plotOptions: { bar: { borderRadius: 4, borderRadiusApplication: 'end' } },
			dataLabels: { enabled: snapshotType === 'bar', formatter: fnValueFormatter },
			grid: {
				borderColor: theme.grid,
				strokeDashArray: 4,
				xaxis: { lines: { show: false } },
				yaxis: { lines: { show: true } },
				...gridPadding
			},
			xaxis: {
				categories: categories.map((category) => (category.length > 0 ? category : '—')),
				axisBorder: { color: theme.grid },
				axisTicks: { color: theme.grid }
			},
			yaxis: { labels: { show: !isArea, formatter: fnValueFormatter } },
			tooltip: { theme: theme.tooltip, y: { formatter: fnValueFormatter } },
			legend: { position: 'bottom', labels: { colors: theme.text } },
			...staticExtras
		};
	}

	/* Not grouped and not bucketed: one bar per metric. */
	return {
		chart: { type: 'bar', height, background: 'transparent', foreColor: theme.text, ...staticChart },
		theme: { mode: theme.mode },
		colors: theme.colors,
		series: [{ name: context.valueLabel, data: series.map((entry) => entry.Points[0]?.Value ?? 0) }],
		plotOptions: { bar: { distributed: true, borderRadius: 4, borderRadiusApplication: 'end' } },
		dataLabels: { formatter: fnValueFormatter },
		grid: {
			borderColor: theme.grid,
			strokeDashArray: 4,
			xaxis: { lines: { show: false } },
			yaxis: { lines: { show: true } },
			...gridPadding
		},
		xaxis: {
			categories: series.map((entry) => entry.Label),
			axisBorder: { color: theme.grid },
			axisTicks: { color: theme.grid }
		},
		yaxis: { labels: { show: !isArea, formatter: fnValueFormatter } },
		tooltip: { theme: theme.tooltip, y: { formatter: fnValueFormatter } },
		legend: { show: false },
		...staticExtras
	};
}
